import React, {useState} from "react";
import decamelizeKeysDeep from "decamelize-keys-deep";
import {damsFetch} from "../app/damsFetch";
import {RelatedObjectCard} from "./RelatedObjectCard";
import {fetchRelationDocs} from "./fetchRelationDocs";
import {
    useSearchResultDetailsState,
    useSearchResultDetailsTranslation,
} from "../search-result-details/SearchResultDetailsContext";
import {LinearProgress, Stack} from "@mui/material";
import Typography from "@mui/material/Typography";
import useDeepCompareEffect from "use-deep-compare-effect";
import {clientLog} from "../clientLog";
import Box from "@mui/material/Box";

/**
 * A component to preview related objects of a given document.
 *
 * Fetches the related objects from the DAMS API and maps them to their
 * corresponding models. The related objects are then rendered as a list of
 * `RelatedObjectCard` components.
 *
 * @returns {React.ReactElement} The `PreviewRelated` component.
 */
export const PreviewRelated = () => {
    const [relatedObjects, setRelatedObjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const t = useSearchResultDetailsTranslation();
    const {objectRelations} = useSearchResultDetailsState();

    /**
     * Creates a parent relation object from a given model.
     *
     * @param {Object} m Model (object) from the search API.
     * @returns {Object} A related object with `relationType = "Inng r i"` and empty `relationDescription`.
     */
    const createParent = (m) => {
        return {
            ...m,
            relationType: "Inngår i",
            relationDescription: "",
        };
    };

    /**
     * Fetches related documents from the DAMS API.
     *
     * @param {string[]} sourceIds - An array of IDs of the related documents
     * @param {object[]} mappedRelations - An array of relations with the related documents
     *
     * @returns {Promise<void>}
     */
    const getRelatedDocuments = async (sourceIds, mappedRelations) => {
        try {
            const query = "unique_id: (" + sourceIds.join(" OR ") + ")";
            const sort = "updated_at desc";
            const searchParams = new URLSearchParams(
                decamelizeKeysDeep({
                    q: query,
                    sort: sort,
                    fl: "*",
                    fq: ['status:("published")'],
                    start: 0,
                    rows: 100,
                    documentType: `(StillImage OR Video OR Audio OR Misc)`,
                    museums: [],
                    onlyMine: false,
                    onlyOthers: false,
                })
            );

            const res = await damsFetch(`/search?${searchParams.toString()}`);
            const {models} = res;
            setRelatedModels(models, mappedRelations);
            setLoading(false);
        } catch (error) {
            clientLog('error', error, 'PreviewRelated');
            setLoading(false);
        }
    };

    /**
     * Sets the related objects given a list of models and their mapped relations.
     *
     * @param {Array<Object>} models List of models (objects) from the search API.
     * @param {Array<Object>} mappedRelations List of object relations with
     *     `reference.sourceId`, `relationType`, and `relationDescription` properties.
     *
     * Maps the given models to the related objects by:
     * - If a child relation is found in `mappedRelations`, add `relationType` and
     *   `relationDescription` properties to the model.
     * - If no child relation is found, create a parent relation with
     *   `relationType = "Inng r i"` and empty `relationDescription`.
     * Then sets the `relatedObjects` state with the mapped models.
     */
    const setRelatedModels = (models, mappedRelations) => {
        const mappedModels = models.map((m) => {
            if (mappedRelations) {
                // Child relation
                const childRelation = mappedRelations.find((r) => {
                    return (
                        r.reference.sourceId === m.uniqueId &&
                        r.relationType &&
                        r.relationDescription
                    );
                });

                if (childRelation) {
                    return {
                        ...m,
                        relationType: childRelation.relationType,
                        relationDescription: childRelation.relationDescription,
                    };
                } else {
                    return createParent(m);
                }
            }
            return createParent(m);
        });
        setRelatedObjects(mappedModels);
    };

    useDeepCompareEffect(() => {
        const sourceIds = objectRelations.map((r) => r.reference.sourceId);
        if (sourceIds.length === 0) {
            setLoading(false);
            return;
        }
        const mappedObjectRelations = objectRelations.filter(
            (r) => typeof r.reference.uniqueId !== "undefined"
        );
        const relationIds = mappedObjectRelations.map((r) => r.reference.uniqueId);
        if (relationIds.length > 0) {
            (async () => {
                const relationDocs = await fetchRelationDocs(relationIds);
                const mappedRelations = objectRelations.map((r) => {
                    return {
                        ...r,
                        relationType: relationDocs.find((f) => {
                            return r.reference.uniqueId === f.uniqueId;
                        }).relationType,
                        relationDescription: relationDocs.find((f) => {
                            return r.reference.uniqueId === f.uniqueId;
                        }).relationDescription,
                    };
                })
                await getRelatedDocuments(sourceIds, mappedRelations);
            })();
        } else {
            // Only parents exists, fetch them.
            (async () => await getRelatedDocuments(sourceIds, null))();
        }
    }, [objectRelations]);

    const getRelatedObjectCard = () => {
        return relatedObjects.map((r) => (
            <RelatedObjectCard model={r} key={r.uniqueId}/>
        ));
    };

    return (
        <>
            {loading && (
                <Stack
                    direction="column"
                    sx={{
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "40px",
                        maxHeight: '200px'
                    }}>
                    <LinearProgress color="secondary" sx={{width: "100%"}}/>
                    <Typography sx={{
                        mt: 1
                    }}>
                        {t("txtLoadingRelatedObjects", "Henter relaterte objekter")}
                    </Typography>
                </Stack>
            )}

            {!loading && relatedObjects.length > 0 && getRelatedObjectCard()}
            {!loading && relatedObjects.length === 0 &&
                <Box sx={{width: '100%', textAlign: 'center'}}>
                    <Typography variant="body2">
                        {t('noRelatedObjects', 'Ingen relaterte objekter')}
                    </Typography>
                </Box>
            }
        </>
    );
};
